import omit from 'lodash/omit';

import { ActionsTab, ActionType } from '@evidentid/tprm-portal-lib/models/entity-actions-review';

import { COMPLETED_ONLY_FILTER_KEYS } from '@/modules/entity-actions-review/constants';
import { ActionsListFilters } from '@/modules/entity-actions-review/types';
import { getActionsListFilterKeys } from '@/modules/entity-actions-review/utils/actionReviewFilter';

type RouteQuery = Record<string, unknown>;

/** Filters whose value is a list in the model and a CSV on the route. */
const listFilterKeys: string[] = [ 'actionTypes' ];

const validActionTypes: string[] = Object.values(ActionType);
// Typed against the model rather than as bare strings, so renaming a literal in
// ActionsListFilters fails to compile here instead of silently dropping the filter.
const validOutcomes: NonNullable<ActionsListFilters['outcome']>[] = [ 'accepted', 'rejected' ];
const validScopes: NonNullable<ActionsListFilters['scope']>[] = [ 'entity', 'global' ];

// The gateway rejects an actionTypes/outcome/scope value it does not recognise with a 400,
// which the store surfaces as an application-wide error page rather than an empty result.
// A hand-edited or stale URL can carry anything for these facets, so unrecognised entries
// are dropped here -- individually for actionTypes, or the whole facet for outcome/scope --
// before the query ever reaches the API.
function readListValue(key: string, value: string): string[] {
    const entries = value.split(',');
    return key === 'actionTypes' ? entries.filter((entry) => validActionTypes.includes(entry)) : entries;
}

function isValidScalar(key: string, value: string): boolean {
    // The arrays carry the model's literal types; widen only for the lookup, since the
    // incoming value is an arbitrary string off the route.
    if (key === 'outcome') {
        return (validOutcomes as string[]).includes(value);
    }
    if (key === 'scope') {
        return (validScopes as string[]).includes(value);
    }
    return true;
}

// A hand-edited URL can carry anything -- an array from a repeated query param, an empty
// string, a key that no longer exists -- so every entry is read defensively and simply
// skipped rather than allowed to throw.
//
// `tab` matters because outcome/resolvedFrom/resolvedTo/resolvedBy only ever match a
// resolution row, which a pending action never has. Without dropping them here, a
// hand-edited or stale `?tab=pending&outcome=accepted` URL sends a resolution facet
// alongside status=pending, gets back zero rows, and shows a message about actions
// completed before revert existed on a tab where nothing has ever had a resolution. The
// tab switch and the filter popover already strip these keys on their own entrances; this
// is the query's, so all three obey the same rule.
export function readFiltersFromQuery(query: RouteQuery, tab: ActionsTab): ActionsListFilters {
    const filters: Record<string, unknown> = {};
    const allowedKeys = tab === ActionsTab.completed
        ? getActionsListFilterKeys()
        : getActionsListFilterKeys().filter((key) => !(COMPLETED_ONLY_FILTER_KEYS as string[]).includes(key));
    for (const key of allowedKeys) {
        const value = query[key];
        if (key !== 'insuredId' && typeof value === 'string' && value !== '') {
            if (listFilterKeys.includes(key)) {
                const validEntries = readListValue(key, value);
                if (validEntries.length > 0) {
                    filters[key] = validEntries;
                }
            } else if (isValidScalar(key, value)) {
                filters[key] = value;
            }
        }
    }
    // insuredId is the legacy spelling of entityId. It is only consulted when entityId
    // itself was not already found above, so an explicit entityId always wins.
    if (!filters.entityId && typeof query.insuredId === 'string' && query.insuredId) {
        filters.entityId = query.insuredId;
    }
    return filters as ActionsListFilters;
}

export function writeFiltersToQuery(query: RouteQuery, filters: ActionsListFilters): RouteQuery {
    const next: RouteQuery = omit(query, getActionsListFilterKeys());
    Object.entries(filters).forEach(([ key, value ]) => {
        if (Array.isArray(value)) {
            if (value.length > 0) {
                next[key] = value.join(',');
            }
        } else if (value) {
            next[key] = value;
        }
    });
    return next;
}
